/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: branding-file-service.ts
 *
 * Created by kyang on 10/21/20.
 */

//
// This service contains all the logic used to
// upload & validate files for branding assets.
//

angular.module('prism-ui.components.branding').service('BrandingFileSelectionService', function (
    this: JS5Object,
    _: JS5Object,
    $q: JS5Object,
    AuthFactory: JS5Object,
    BrandingAssetService: JS5Object,
    BrandingViewService: JS5Object,
    EndPointService: JS5Object,
    MediaProfileResourceFactory: JS5Object,
    Rx: JS5Object,
) {
    enum ContainerType {
        PRE_ROLL = 'preRolls',
        POST_ROLL = 'postRolls',
        BREAK_PRE_POST_ROLL = 'breakPrePostRolls',
        ARTWORK = 'artwork',
        WATERMARK = 'watermarks',
        RATING_OVERLAY = 'ratingOverlays',
    }

    this.getAssetAcceptProfile = (asset: JS5Object, assetType: string) => {
        switch(assetType) {
            case 'watermark':
                return asset.videoAcceptProfile;
            case 'ratingOverlay':
                return asset.mediaAcceptProfile;
            default:
                return asset.acceptProfile;
        }
    };

    this.onFileSelected = (assetType: string, rootAsset: JS5Object, asset: JS5Object, virtualPath: string) => {
        asset.virtualPath = virtualPath;
        _.unset(asset, 'assetId');
        _.unset(asset, 'asset');
        _.unset(asset, 'contents');
        _.unset(asset, 'proxyId');
        if (assetType === 'artwork') {
            //
            // Spaghetti: at this point, `asset` is
            // just the MAP entry in the artwork
            // container.
            //
            if (rootAsset.seasonId) {
                asset.seasonId = rootAsset.seasonId;
            } else if (rootAsset.volumeId) {
                asset.volumeId = rootAsset.volumeId;
            }
        }
    }


    //==========================//
    // Remote Upload Validation //
    //==========================//

    const getVideoAsset = (containerType: string, assetType: string, rootAsset: JS5Object, asset: JS5Object) => {
        if (containerType === ContainerType.PRE_ROLL || containerType === ContainerType.POST_ROLL) {
            return rootAsset;
        } else if (containerType === ContainerType.BREAK_PRE_POST_ROLL) {
            let preRollAssets: JS5Object[];
            switch (assetType) {
                case 'video':
                    return asset;
                case 'audio':
                    preRollAssets = rootAsset.preRoll.audios;
                    break;
                case 'cc':
                    preRollAssets = rootAsset.preRoll.texts;
                    break;
                default:
                    return null;
            }
            return (_.includes(preRollAssets, asset)) ? rootAsset.preRoll : rootAsset.postRoll;
        }
        return null;
    };

    const validateTextAsset = (videoAsset: JS5Object, asset: JS5Object, virtualPath: string) => {
        let textQueryParams = {
            ccPath: virtualPath,
            prePostRollId: videoAsset.id
        };

        return MediaProfileResourceFactory.validateClosedCaption(textQueryParams, asset).$promise
            .then((validationResult: JS5Object) => {
                return validationResult.failures;
            });
    };

    const validateWatermark = (availableAcceptProfiles: JS5Object[], asset: JS5Object, virtualPath: string) => {
        // We don't, at this time, validate watermarks.
        return $q.when([/* Empty validation failure list */]);
    };

    const validateRatingOverlayImage = (availableAcceptProfiles: JS5Object[], asset: JS5Object, virtualPath: string) => {
        // We don't, at this time, validate rating overlay images.
        return $q.when([/* Empty validation failure list */]);
    };

    const validateNonTextAsset = (containerType: string, acceptProfileId: number, asset: JS5Object, virtualPath: string) => {
        return BrandingAssetService.validateNonTextAsset(containerType, acceptProfileId, asset, virtualPath);
    };

    // Errors & warnings are objects with:
    // {
    //     description: string, severity: 'WARNING' | 'ERROR',
    // From server:
    //     source: string, error: string, property: string, actualValue: string, expectedValue: string
    // }
    let parseValidationFailures = (failures: JS5Object[], assetPath: string) => {
        let sortedValidation = {source: assetPath, errors: [], warnings: []};
        angular.forEach(failures, (failure: JS5Object) => {
            if (failure.property && failure.actualValue && failure.expectedValue) {
                failure.description = failure.property + ': Actual value ' + failure.actualValue + ', expected value ' + failure.expectedValue;
            } else {
                failure.description = failure.source + ': ' + failure.description;
            }

            if (failure.severity === 'WARNING') {
                sortedValidation.warnings.push(failure);
            } else {
                sortedValidation.errors.push(failure);
            }
        });
        return sortedValidation;
    };

    const runRemoteValidationChecks = (
        containerType: string,
        assetType: string,
        rootAsset: JS5Object,
        availableAcceptProfiles: JS5Object[],
        asset: JS5Object,
        virtualPath: string
    ) => {
        let videoAsset = getVideoAsset(containerType, assetType, rootAsset, asset);
        let validationPromise;

        if (assetType === 'cc') {
            validationPromise = validateTextAsset(videoAsset, asset, virtualPath);
        } else if (assetType === 'watermark') {
            validationPromise = validateWatermark(availableAcceptProfiles, asset, virtualPath);
        } else if (assetType === 'ratingOverlay') {
            validationPromise = validateRatingOverlayImage(availableAcceptProfiles, asset, virtualPath);
        } else {
            validationPromise = validateNonTextAsset(
                containerType,
                this.getAssetAcceptProfile(asset, assetType).id,
                asset,
                virtualPath
            );
        }

        return validationPromise.then((validationFailures: JS5Object[]) => {
            if (!validationFailures.length) {
                return {source: virtualPath, errors: [], warnings: []};
            } else {
                return parseValidationFailures(validationFailures, virtualPath);
            }
        });
    };

    this.validateRemoteFile = (
        containerType: string,
        assetType: string,
        rootAsset: JS5Object,
        availableAcceptProfiles: JS5Object[],
        asset: JS5Object,
        fileSystemItem: JS5Object
    ) => {
        let progressObservable = new Rx.BehaviorSubject(0.01);
        let virtualPath = fileSystemItem.parent + fileSystemItem.name;
        let validationPromise = runRemoteValidationChecks(
            containerType,
            assetType,
            rootAsset,
            availableAcceptProfiles,
            asset,
            virtualPath
        ).then((validationResult: JS5Object) => {
            progressObservable.onNext(1);
            progressObservable.onCompleted();
            return validationResult;
        }).catch((error: any) => {
            progressObservable.onCompleted();
            return {
                source: virtualPath,
                errors: [{
                    description: _.toString(error),
                    severity: 'ERROR',
                }],
                warnings: [],
            };
        });
        return {
            promise: validationPromise,
            progressObservable,
        };
    };


    //===================//
    // Local File Upload //
    //===================//

    this.reinitializeLocalUploaderConfig = (assetType: string, uploaderConfig: JS5Object) => {
        // TODO: Call TempFileUploadService instead. Would need to move service to another module to avoid circular dependencies. <components.data?>
        uploaderConfig.url = EndPointService.fileUploadEndPoint;
        uploaderConfig.alias = 'multipart-content';
        uploaderConfig.autoUpload = false;
        uploaderConfig.formData = {};
        uploaderConfig.headers = {
            'Authorization': AuthFactory.getAuthHeader(),
            'Accept': 'application/json, text/plain, */*'
        };

        let typeFilter = _.find(uploaderConfig.filters, {name: 'fileTypeFilter'});
        if (!typeFilter.origFn) {
            typeFilter.origFn = typeFilter.fn;
        }
        // TODO: AB#88420 Once file-select checks fileTypes, remove this override
        typeFilter.fn = (file: File, options: JS5Object) => {
            let type = file.type.slice(file.type.lastIndexOf('/') + 1).toLowerCase();
            if (assetType === 'video') {
                typeFilter.errorDescription = 'File Type not valid!';
                return type === 'quicktime';
            } else if (assetType === 'cc') {
                typeFilter.errorDescription = 'File Type not valid!';
                return type === '';
            }
            return typeFilter.origFn(file, options);
        };
    };

    const isImageSizeValid = (availableAcceptProfiles: JS5Object[], img: JS5Object, property: string) => {
        let anySizeAllowed = true;
        let imgSize = img[property];
        return _.some(availableAcceptProfiles, (acceptProfile: JS5Object) => {
            let rangesStr = _.get(acceptProfile, property);
            if (!imgSize) {
                return false;
            }
            if (!rangesStr) {
                return true;
            }
            let ranges = rangesStr.split(',');
            return _.some(ranges, (range: string) => {
                let separatorIndex = range.indexOf('..');
                let min = Number.MIN_VALUE;
                let max = Number.MAX_VALUE;
                anySizeAllowed = false;
                if (separatorIndex < 0) {
                    min = parseInt(range);
                    max = min;
                } else if (separatorIndex === 0) {
                    max = parseInt(range.substring(separatorIndex +2));
                } else if (separatorIndex < range.length -2) {
                    min = parseInt(range.substring(0, separatorIndex));
                    max = parseInt(range.substring(separatorIndex +2));
                } else {
                    min = parseInt(range.substring(0, separatorIndex));
                }
                return min <= img[property] && imgSize <= max;
            });
        }) || anySizeAllowed;
    };

    const setupLocalImageValidationChecks = (availableAcceptProfiles: JS5Object[], reader: FileReader, deferredValidation: JS5Object) => {
        reader.onload = () => {
            let img = new Image();
            img.onload = () => {
                let errors = [];
                let validWidths = _.chain(availableAcceptProfiles)
                    .map((acceptProfile: JS5Object) => acceptProfile.width ? acceptProfile.width.split(',') : [])
                    .flatten()
                    .uniq()
                    .join(',')
                    .value();
                let validHeights = _.chain(availableAcceptProfiles)
                    .map((acceptProfile: JS5Object) => acceptProfile.height ? acceptProfile.height.split(',') : [])
                    .flatten()
                    .uniq()
                    .join(',')
                    .value();

                if (!isImageSizeValid(availableAcceptProfiles, img, 'width')) {
                    errors.push({
                        description: `Browser check: File width not valid! (${img.width} did not match the expected value: ${validWidths})`,
                        severity: 'ERROR'
                    });
                }
                if (!isImageSizeValid(availableAcceptProfiles, img, 'height')) {
                    errors.push({
                        description: `Browser check: File height not valid! (${img.height} did not match the expected value: ${validHeights})`,
                        severity: 'ERROR'
                    });
                }
                deferredValidation.resolve({errors, warnings: []});
            }
            img.onerror = (ev) => {
                deferredValidation.resolve({
                    errors: [],
                    warnings: [{
                        description: 'Browser could not check the size of the image',
                        severity: 'WARNING',
                    }],
                });
            }
            img.src = reader.result;
        };
    };

    const skipLocalValidationChecks = (reader: FileReader, deferredValidation: JS5Object) => {
        reader.onload = () => {
            deferredValidation.resolve({errors: [], warnings: []});
        };
    };

    const runLocalValidationChecks = (assetType: string, availableAcceptProfiles: JS5Object[], asset: JS5Object, fileItem: JS5Object) => {
        let reader = new FileReader();
        let progressObservable = new Rx.BehaviorSubject(0);
        let deferredValidation = $q.defer();
        switch(assetType) {
            case 'watermark':
                setupLocalImageValidationChecks(availableAcceptProfiles, reader, deferredValidation);
                break;
            case 'artwork':
                setupLocalImageValidationChecks([
                    this.getAssetAcceptProfile(asset, assetType)
                ], reader, deferredValidation);
                break;
            case 'ratingOverlay':
            case 'audio':
            case 'video':
            default:
                skipLocalValidationChecks(reader, deferredValidation);
                break;
        }

        reader.onprogress = (event: JS5Object) => {
            progressObservable.onNext(event.loaded / event.total);
        };
        reader.readAsDataURL(fileItem._file);

        return {
            progressObservable,
            promise: deferredValidation.promise.then((validationMessages: JS5Object) => {
                if (!validationMessages.errors.length) {
                    progressObservable.onNext(1);
                }
                progressObservable.onCompleted();
                return {
                    source: fileItem.file.name,
                    uploadedData: reader.result,
                    errors: validationMessages.errors,
                    warnings: validationMessages.warnings,
                };
            })
        };
    }

    const uploadAndRunRemoteValidationChecks = (
        containerType: string,
        assetType: string,
        rootAsset: JS5Object,
        availableAcceptProfiles: JS5Object[],
        asset: JS5Object,
        uploaderConfig: JS5Object,
    ) => {
        let progressObservable = new Rx.BehaviorSubject(0);
        let deferredResult = $q.defer();
        uploaderConfig.onProgressItem = (fileItem: JS5Object, progress: number) => {
            progressObservable.onNext(progress/100 /2);
        }
        uploaderConfig.onSuccessItem = (fileItem: JS5Object, response: JS5Object) => {
            progressObservable.onNext(0.5);
            deferredResult.resolve(runRemoteValidationChecks(
                containerType,
                assetType,
                rootAsset,
                availableAcceptProfiles,
                asset,
                response.path
            ).then((validationResult: JS5Object) => {
                progressObservable.onNext(1);
                progressObservable.onCompleted();
                validationResult.uploadedData = response.path;
                return validationResult;
            }).catch((error: any) => {
                progressObservable.onCompleted();
                return $q.reject(error);
            }));
        };
        uploaderConfig.onErrorItem = (fileItem: JS5Object, response: JS5Object, status: string, headers: JS5Object) => {
            progressObservable.onCompleted();
            deferredResult.reject(`Failed to upload file. Error code: ${status}`);
        };

        uploaderConfig.uploadAll();
        return {
            promise: deferredResult.promise,
            progressObservable
        };
    };

    this.validateLocalFile = (
        containerType: string,
        assetType: string,
        rootAsset: JS5Object,
        availableAcceptProfiles: JS5Object[],
        asset: JS5Object,
        fileItem: JS5Object,
        uploaderConfig: JS5Object,
    ) => {
        let localValidation = runLocalValidationChecks(assetType, availableAcceptProfiles, asset, fileItem);
        let uploadValidation = uploadAndRunRemoteValidationChecks(
            containerType,
            assetType,
            rootAsset,
            availableAcceptProfiles,
            asset,
            uploaderConfig
        );
        let stopProgressObservable = new Rx.Subject();
        let progressObservable = localValidation.progressObservable
            .takeUntil(stopProgressObservable)
            .combineLatest(uploadValidation.progressObservable, (localProgress: number, remoteProgress: number) => {
                return (localProgress + remoteProgress) /2;
            });
        let validationPromise = $q.all({
            local: localValidation.promise,
            remote: uploadValidation.promise,
        }).then((results: JS5Object) => {
            stopProgressObservable.onCompleted();
            return {
                source: results.local.source,
                uploadedData: {
                    loadedData: results.local.uploadedData,
                    remotePath: results.remote.uploadedData,
                },
                errors: results.local.errors.concat(results.remote.errors),
                warnings: results.local.warnings.concat(results.remote.warnings),
            };
        }).catch((error: any) => {
            stopProgressObservable.onCompleted();
            return {
                source: fileItem.file.name,
                uploadedData: {},
                errors: [{
                    description: _.toString(error),
                    severity: 'ERROR',
                }],
                warnings: [],
            };
        });

        return {
            promise: validationPromise,
            progressObservable,
        };
    };
});
